<template>

    <ajax-table :data="data"
                :headers="headers"
                v-model:loading="ajaxTable.loading"
                v-model:search="ajaxTable.search"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:page-info="ajaxTable.pageInfo"
                :exportable="false"
                @loadData="getData"
                no-data-message="No users found"
                loading-message="Loading users"
    >

        <template #above_table_center>
            <div class="flex gap-3">


                <a v-if="permissions.can('create_users')" class="flex w-full items-center justify-center gap-2 rounded-md border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:border-green-700 hover:bg-green-700 hover:text-white hover:no-underline sm:max-w-80"
                   :href="company.buildCompanyRoute('users/create')"
                >
                    <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/><path d="M16 19h6"/><path d="M19 16v6"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4"/></svg>
                    </span>
                    Create User
                </a>


                <div class="relative">
                    <button type="button"
                            class="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 hover:bg-gray-50 sm:max-w-80"
                            @click="toggleActionsMenu"
                    >
                        Actions
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6"/></svg>
                    </button>

                    <div v-if="showActionsMenu" class="fixed inset-0 z-10" @click="showActionsMenu = false"></div>

                    <div v-if="showActionsMenu" class="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-md border border-gray-200 bg-white shadow-lg">

                        <a v-if="permissions.can('import_users')"
                           class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-700 hover:no-underline"
                           :href="company.buildCompanyRoute('import-users')"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M5 13v-8a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5.5m-9.5 -2h7m-3 -3l3 3l-3 3"/></svg>
                            Import Users
                        </a>

                        <div class="border-t border-gray-100 px-4 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                            Copy emails by permission level
                        </div>
                        <div class="max-h-72 overflow-y-auto pb-1">

                            <div v-if="emailGroupsLoading" class="px-4 py-3 text-sm text-gray-500">
                                Loading&hellip;
                            </div>

                            <template v-else-if="emailGroups && emailGroups.length">
                                <button v-for="group in emailGroups" :key="group.name"
                                        type="button"
                                        class="flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                        @click="copyLevelEmails(group)"
                                >
                                    <span>{{ group.display_name }}</span>
                                    <span v-if="copiedLevel === group.name" class="flex items-center gap-1 text-xs font-medium text-green-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                                        Copied
                                    </span>
                                </button>
                            </template>

                            <div v-else class="px-4 py-3 text-sm text-gray-500">
                                No users found
                            </div>

                        </div>
                    </div>
                </div>

            </div>
        </template>

        <template #col-1="props">
            <div class="flex flex-col items-start justify-center">
                <div class="text-gray-700 space-y-2">

                    <div class="text-base">{{ props.row.display_name }}</div>

                    <div class="ml-2 flex flex-col gap-1">
                        <div class="flex items-center gap-1 text-sm text-gray-500">

                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"/><path d="M3 7l9 6l9 -6"/></svg>
                            </span>
                            {{ props.row.email }}

                        </div>

                        <div class="flex items-center gap-1 text-sm text-gray-500">

                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 21v-15c0 -1 1 -2 2 -2h5c1 0 2 1 2 2v15"/><path d="M16 8h2c1 0 2 1 2 2v11"/><path d="M3 21h18"/><path d="M10 12v0"/><path d="M10 16v0"/><path d="M10 8v0"/><path d="M7 12v0"/><path d="M7 16v0"/><path d="M7 8v0"/><path d="M17 12v0"/><path d="M17 16v0"/></svg>
                            </span>
                            {{ props.row.company}} <span v-if="permissions.isSonicu()">({{ props.row.company_id }})</span>

                        </div>
                    </div>

                </div>
            </div>
        </template>

        <template #col-2="props">
            <div class="flex flex-col items-start justify-center">
                <div class="space-y-2">

                    <span v-if="props.row.notifications?.notification_enabled" class="inline-flex items-center rounded-md bg-green-100 px-3 text-xs font-medium text-green-800 gap-x-1.5 py-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-check"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                        Enabled
                    </span>

                    <span v-else class="inline-flex items-center rounded-md bg-red-100 px-3 text-xs font-medium text-red-800 gap-x-1.5 py-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>
                        Disabled
                    </span>

                    <div v-if="props.row.notifications?.notification_enabled" class="flex gap-2 text-gray-500">

                        <span v-if="props.row.notifications?.email">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"/><path d="M3 7l9 6l9 -6"/></svg>
                        </span>

                        <span v-if="props.row.notifications?.sms">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M11 3h10v8h-3l-4 2v-2h-3z"/><path d="M15 16v4a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1h2"/><path d="M10 18v.01"/></svg>
                        </span>

                        <span v-if="props.row.notifications?.voice">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5-2.5l5 2v4a2 2 0 0 1-2 2a16 16 0 0 1-15-15a2 2 0 0 1 2-2"/></svg>
                        </span>

                        <span v-if="props.row.notifications?.push">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 18.004h-8.343c-2.572-.004-4.657-2.011-4.657-4.487c0-2.475 2.085-4.482 4.657-4.482c.393-1.762 1.794-3.2 3.675-3.773c1.88-.572 3.956-.193 5.444 1c1.488 1.19 2.162 3.007 1.77 4.769h.99c1.374 0 2.562.805 3.121 1.972"/><path d="M19 16v3"/><path d="M19 22v.01"/></svg>
                        </span>

                        <span v-if="props.row.notifications && hasNoNotificationMethods(props.row.notifications)" class="text-sm">
                            Notification methods not set
                        </span>
                    </div>

                </div>
            </div>
        </template>

        <template #col-3="props">
            <div class="flex flex-col items-start justify-center">
                <div class="text-gray-700">

                    {{ props.row.permission_level }}

                </div>
            </div>
        </template>

        <template #col-4="props">
            <div class="flex flex-col items-start justify-center">
                <div class="text-gray-700">

                    {{ props.row.last_login }}

                </div>
            </div>
        </template>

        <template #col-5="props">
            <div class="flex w-full flex-col items-end justify-start">
                <div class="flex flex-row gap-4">

                    <button class="transition-colors duration-150 hover:text-blue-600"
                            @click="showUser(props.row)"
                            @click.middle="showUser(props.row, true)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" /></svg>
                    </button>

                    <button v-if="permissions.can('update_users')" class="transition-colors duration-150 hover:text-green-600"
                            @click="editUser(props.row)"
                            @click.middle="editUser(props.row, true)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" /><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" /><path d="M16 5l3 3" /></svg>
                    </button>

                    <div v-if="permissions.can('login_as_other_user')" class="transition-colors duration-150 hover:text-blue-600" :data-tippy-content="`Login as ${props.row.display_name}`"
                         @click="loginAsUser(props.row)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M13.192 9h6.616a2 2 0 0 1 1.992 2.183l-.567 6.182a4 4 0 0 1 -3.983 3.635h-1.5a4 4 0 0 1 -3.983 -3.635l-.567 -6.182a2 2 0 0 1 1.992 -2.183z" /><path d="M15 13h.01" /><path d="M18 13h.01" /><path d="M15 16.5c1 .667 2 .667 3 0" /><path d="M8.632 15.982a4.037 4.037 0 0 1 -.382 .018h-1.5a4 4 0 0 1 -3.983 -3.635l-.567 -6.182a2 2 0 0 1 1.992 -2.183h6.616a2 2 0 0 1 2 2" /><path d="M6 8h.01" /><path d="M9 8h.01" /><path d="M6 12c.764 -.51 1.528 -.63 2.291 -.36" /></svg>
                    </div>

                </div>
            </div>
        </template>


    </ajax-table>
</template>

<script>

import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Pagination from "@/components/Pagination"
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect"
import FloatingInput from "@/components/elements/FloatingInput";
import Select2 from '@/components/elements/select2'
import GreenButton from "@/components/elements/green-button";
import UserNotificationsShow from "@/components/Users/UserNotificationsShow";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import AjaxTable from "@/components/elements/AjaxTable.vue";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import tippy from 'tippy.js';
import _ from "lodash";

export default {
    name: "UserList",
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin],
    components: {
        AjaxTable,
        ButtonBlueSecondary,
        UserNotificationsShow, GreenButton, Pagination, ItemsPerPageSelect, FloatingInput, Select2
    },
    props: {
        companyList: {},
    },
    data() {
        return {
            data: [],
            headers: [
                {
                    name: 'Company',
                    sortBy: '',
                    show: 'sm',
                    key: 'company',
                },
                {
                    name: 'Notifications',
                    sortBy: '',
                    show: 'md',
                    key: 'notifications',
                },
                {
                    name: 'Permission Level',
                    sortBy: '',
                    show: 'md',
                    key: 'permission_level',
                },
                {
                    name: 'Last Logged In',
                    sortBy: '',
                    show: 'md',
                    key: 'last_login',
                },
                {
                    name: '',
                    sortBy: '',
                    show: 'sm',
                    key: ''
                }
            ],
            requestNumber: 0,
            processing: false,

            tippyInstance: null,

            showActionsMenu: false,
            emailGroups: null,
            emailGroupsLoading: false,
            copiedLevel: null,
            copiedLevelTimeout: null,
        }
    },
    methods: {
        getData() {
            this.ajaxTable.loading = true
            this.requestNumber++
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.requestNumber = this.requestNumber
            axios.get(this.company.buildCompanyApiInternalRoute('users'),
                {
                    params: params,
                }
            ).then(response => {
                let data = response.data;

                // The API echoes requestNumber back as a string, so coerce before the
                // strict compare — otherwise "1" !== 1 skips every response.
                if (`${data.meta.requestNumber}` !== `${this.requestNumber}`) {
                    return
                }
                this.ajaxUpdateTableData(data);
                this.data = !_.isNil(data.data) ? data.data : [];
            }).catch(error => {
                console.log(error)
            }).then(() => {
                this.ajaxTable.loading = false
                this.tippyInstance = tippy('[data-tippy-content]', {theme: 'material'})
            })
        },
        loginAsUser(user) {
            if (!user.id) return;

            axios.get('/SonicuTools/admin-login-as-user', {params: {id: user.id}})
                .then(() => {
                    alert(`You are now logged in as ${user.display_name}! Please log out when finished testing.`);
                    window.location.href = `/${user.company_id}/login`;
                })
                .catch(() => {
                    alert(`Error logging into ${user.display_name}`);
                })

        },
        showUser(user, newTab = false) {
            let urlPartial = this.company.buildCompanyRoute(`users/${user.id}`)

            if (newTab) {
                window.open(`${urlPartial}`, '_blank')
            } else {
                window.location = urlPartial
            }
        },
        editUser(user, newTab = false) {
            let urlPartial = this.company.buildCompanyRoute(`users/${user.id}/edit`)

            if (newTab) {
                window.open(`${urlPartial}`, '_blank')
            } else {
                window.location = urlPartial
            }
        },
        hasNoNotificationMethods(notifications) {
            return !notifications.email && !notifications.sms && !notifications.voice && !notifications.push
        },
        toggleActionsMenu() {
            this.showActionsMenu = !this.showActionsMenu
            // Fetch the email groups lazily each time the menu is opened so the list stays current
            // with any users added/removed since the last look.
            if (this.showActionsMenu) {
                this.fetchEmailGroups()
            }
        },
        fetchEmailGroups() {
            this.emailGroupsLoading = true
            axios.get(this.company.buildCompanyApiInternalRoute('users/emails-by-permission-level'),
                {
                    params: {selectedCompany: this.ajaxTable.selectedCompany},
                }
            ).then(response => {
                this.emailGroups = !_.isNil(response.data.data) ? response.data.data : []
            }).catch(error => {
                console.log(error)
                this.emailGroups = []
            }).then(() => {
                this.emailGroupsLoading = false
            })
        },
        copyLevelEmails(group) {
            const text = (group.emails || []).join(', ')
            this.copyToClipboard(text).then(() => {
                this.copiedLevel = group.name
                if (this.copiedLevelTimeout) clearTimeout(this.copiedLevelTimeout)
                this.copiedLevelTimeout = setTimeout(() => {
                    this.copiedLevel = null
                    this.showActionsMenu = false
                }, 1200)
            }).catch(() => {
                alert('Unable to copy emails to your clipboard.')
            })
        },
        copyToClipboard(text) {
            // Prefer the async Clipboard API (available over https/ngrok); fall back to a temporary
            // textarea + execCommand for insecure contexts (e.g. http://localhost).
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(text)
            }
            return new Promise((resolve, reject) => {
                try {
                    const textarea = document.createElement('textarea')
                    textarea.value = text
                    textarea.style.position = 'fixed'
                    textarea.style.opacity = '0'
                    document.body.appendChild(textarea)
                    textarea.focus()
                    textarea.select()
                    const ok = document.execCommand('copy')
                    document.body.removeChild(textarea)
                    ok ? resolve() : reject()
                } catch (e) {
                    reject(e)
                }
            })
        }
    },
    computed: {}
}
</script>